import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { defaultDatePickerStrings } from "@fluentui/react-datepicker-compat";
import { useToastGlobal } from "../../../stores/useToastGlobal";
import {
    COLLABORATEURS,
    EXPORT_DETAIL_GROUPS,
    EXPORT_FORMATS,
    EXPORT_SYNTHESE_OPTIONS,
} from "../constants";

const COMMON_OPTIONS = { returnObjects: true, ns: "common" };

export const EXPORT_TABS = {
    synthese: "synthese",
    detail: "detail",
};

const buildDetailState = () =>
    EXPORT_DETAIL_GROUPS.reduce((acc, group) => {
        group.options.forEach((opt) => {
            acc[opt.key] = !!opt.checked;
        });
        return acc;
    }, {});

const pad = (n) => String(n).padStart(2, "0");
export const formatDate = (d) =>
    d ? `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}` : "";

/** Logique de la modale "Exporter les données" */
const useExportDialog = () => {
    const { t } = useTranslation();
    const setShowToast = useToastGlobal((s) => s.setShow);

    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState(EXPORT_TABS.synthese);

    // période & format
    const [from, setFrom] = useState(new Date(new Date().getFullYear(), 0, 1));
    const [to, setTo] = useState(new Date());
    const [format, setFormat] = useState(EXPORT_FORMATS[0]);

    // périmètre
    const [departements, setDepartements] = useState([]);
    const [agences, setAgences] = useState([]);
    const [managers, setManagers] = useState([]);
    const [collaborateurs, setCollaborateurs] = useState([]);

    // colonnes
    const [synthese, setSynthese] = useState(() =>
        Object.fromEntries(EXPORT_SYNTHESE_OPTIONS.map((k) => [k, true]))
    );
    const [detail, setDetail] = useState(buildDetailState);

    const localizedStrings = useMemo(
        () => ({
            ...defaultDatePickerStrings,
            days: t("temporal.days", COMMON_OPTIONS),
            shortDays: t("temporal.shortDays", COMMON_OPTIONS),
            months: t("temporal.months", COMMON_OPTIONS),
            shortMonths: t("temporal.shortMonths", COMMON_OPTIONS),
            goToToday: t("temporal.goToToday", COMMON_OPTIONS),
        }),
        [t]
    );

    /* Collaborateurs filtrés par les départements sélectionnés */
    const collaborateursDisponibles = useMemo(
        () =>
            COLLABORATEURS.filter(
                (c) => !departements.length || departements.includes(c.dept)
            ).map((c) => ({ key: c.name, header: c.name, content: c.dept })),
        [departements]
    );

    const onChangeDepartements = (values) => {
        setDepartements(values);
        // retirer les collaborateurs qui ne sont plus dans le périmètre
        if (values.length) {
            setCollaborateurs((prev) =>
                prev.filter((name) => {
                    const c = COLLABORATEURS.find((x) => x.name === name);
                    return c && values.includes(c.dept);
                })
            );
        }
    };

    const toggleSynthese = (key) =>
        setSynthese((prev) => ({ ...prev, [key]: !prev[key] }));

    const toggleDetail = (key) =>
        setDetail((prev) => ({ ...prev, [key]: !prev[key] }));

    const isGroupAllChecked = (group) =>
        group.options.every((opt) => detail[opt.key]);

    const toggleGroup = (group) => {
        const all = isGroupAllChecked(group);
        setDetail((prev) => {
            const next = { ...prev };
            group.options.forEach((opt) => {
                next[opt.key] = !all;
            });
            return next;
        });
    };

    const reset = () => {
        setTab(EXPORT_TABS.synthese);
        setDepartements([]);
        setAgences([]);
        setManagers([]);
        setCollaborateurs([]);
        setDetail(buildDetailState());
    };

    const close = () => {
        setOpen(false);
        reset();
    };

    const exportData = () => {
        setOpen(false);
        setShowToast({
            visible: true,
            level: "success",
            content: "Export généré — fonction illustrée dans la maquette",
            auto_hide_timeout: 2400,
        });
        reset();
    };

    return {
        dialog: { open, openDialog: () => setOpen(true), close },
        tab: { value: tab, set: setTab },
        periode: { from, setFrom, to, setTo, localizedStrings, formatDate },
        format: { value: format, set: setFormat },
        perimetre: {
            departements,
            setDepartements: onChangeDepartements,
            agences,
            setAgences,
            managers,
            setManagers,
            collaborateurs,
            setCollaborateurs,
            collaborateursDisponibles,
        },
        colonnes: {
            synthese,
            toggleSynthese,
            detail,
            toggleDetail,
            isGroupAllChecked,
            toggleGroup,
        },
        exportData,
    };
};

export default useExportDialog;
